import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Pagination,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import MessageIcon from '@mui/icons-material/Message';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import axios from 'axios';
import format from 'date-fns/format';
import startOfToday from 'date-fns/startOfToday';
import subDays from 'date-fns/subDays';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import { useWebSocket } from '../../WebsocketManager/WebSocketContext';
import { advanceSearch } from '../../_helper/services/advanceSearchv2';

const filters = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'lastWeek', label: 'Last Week' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'custom', label: 'Custom Range' },
];

const ActivityPage = ({showFilter}) => {
  const [activities, setActivities] = useState([]);
  const [filter, setFilter] = useState('');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [additionalWhere, setAdditionalWhere] = useState("")
  const itemsPerPage = 15;

  const [response, setResponse] = useState([]);
  const socketData = useWebSocket();

  useEffect(() => {
    if (!socketData) return;
    let tempArr = [];
    tempArr.push(socketData);
    setActivities([...tempArr, ...response]);
  }, [socketData]);

  const payload = {
    id: "fn_report_to_notify_employee",
    location: "global",
    tableName: "fn_report_to_notify_employee",
    searchParams: "[]",
    columns: "*",
    pageNumber: 1,
    pageSize: 999,
    sortData: "",
    additionalWhere: additionalWhere,
    qID: 0,
  };

  const firstCallNotificationRef = useRef(true);

  const getAllNotifications = async () => {
    try {
      setLoading(true);
      const response = await advanceSearch(payload, 'dashboard_notifications' || 'fn_report_to_notify_employee');
      const { results: filteredActivities } = response;
      setActivities(filteredActivities);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
      firstCallNotificationRef.current = false;
    }
  };

  useEffect(() => {
    getAllNotifications();
  }, []);

  useEffect(() => {
    fetchActivities();
  }, [filter, customStartDate, customEndDate, currentPage]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const response = await advanceSearch(payload, 'Report-to-Notify-Employee' || 'fn_report_to_notify_employee');
      let { results: filteredActivities } = response;

      const today = startOfToday();
      if (filter === 'today') {
        filteredActivities = filteredActivities.filter(activity =>
          new Date(activity.NotifiedOn) >= startOfToday()
        );
      } else if (filter === 'yesterday') {
        const yesterday = subDays(today, 1);
        filteredActivities = filteredActivities.filter(activity =>
          new Date(activity.NotifiedOn) >= yesterday && new Date(activity.NotifiedOn) < today
        );
      } else if (filter === 'lastWeek') {
        const lastWeek = subDays(today, 7);
        filteredActivities = filteredActivities.filter(activity =>
          new Date(activity.NotifiedOn) >= lastWeek
        );
      } else if (filter === 'thisMonth') {
        const startOfThisMonth = startOfMonth(today);
        filteredActivities = filteredActivities.filter(activity =>
          new Date(activity.NotifiedOn) >= startOfThisMonth && new Date(activity.NotifiedOn) <= today
        );
      } else if (filter === 'lastMonth') {
        const startOfLastMonth = startOfMonth(subDays(today, 30));
        const endOfLastMonth = endOfMonth(subDays(today, 30));
        filteredActivities = filteredActivities.filter(activity =>
          new Date(activity.NotifiedOn) >= startOfLastMonth && new Date(activity.NotifiedOn) <= endOfLastMonth
        );
      } else if (filter === 'custom' && customStartDate && customEndDate) {
        filteredActivities = filteredActivities.filter(activity =>
          new Date(activity.NotifiedOn) >= customStartDate && new Date(activity.NotifiedOn) <= customEndDate
        );
      }

      setActivities(filteredActivities);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  const filterChangeHandler = (e) => {
    setFilter(e.target.value);
    // const val = e.target.value
    // switch (val){
    //     case "today" : 
    //        setAdditionalWhere(`NotifiedOn >= '${formatDate(new Date())}'`)
    //     break;
    //     case "yesterday" : 
    //     const yesterday = new Date();
    //         yesterday.setDate(yesterday.getDate() - 1);
    //         setAdditionalWhere(`NotifiedOn = '${formatDate(yesterday)}'`);
    //         break;
         
    // }
  }

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const start = (currentPage - 1) * itemsPerPage;
  const currentActivities = activities.slice(start, start + itemsPerPage);

  return (
    <Box my={4}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={4}>
        <Box sx={{ flex: 1, maxHeight: '80vh', overflow: 'auto', scrollbarWidth: 'none', '-ms-overflow-style': 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
            </Box>
          ) : currentActivities.length > 0 ? (
            <List>
              {currentActivities.map((activity, index) => (
                <ListItem
                  key={index}
                  alignItems="flex-start"
                  sx={{ backgroundColor: '#f9f9f9', borderBottom: '1px solid #ddd', mb: 1 }}
                >
                  <ListItemIcon>
                    <MessageIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={activity.NotificationText}
                    secondary={
                      <>
                        <Box display="flex" justifyContent="space-between">
                          <Typography>HR Control: {activity?.HR_CONTROL??activity?.HrControl??activity?.SourceId}</Typography>
                          <Tooltip title={format(new Date(activity.NotifiedOn), 'PPpp')}>
                            <Box display="flex" alignItems="center">
                              <AccessTimeIcon sx={{ mr: 1 }} />
                              <Typography variant="body2" color="textSecondary">
                                {formatDistanceToNow(new Date(activity.NotifiedOn), { addSuffix: true })}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </Box>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography variant="h6" color="textSecondary">
                No notifications available.
              </Typography>
            </Box>
          )}
        </Box>

        {showFilter && 
        <Box ml={4} sx={{ minWidth: 250, position: 'sticky', top: 20 }}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel>Filter</InputLabel>
          <Select
            value={filter}
            onChange={(e) => filterChangeHandler(e)}
            label="Filter"
          >
            {filters.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {filter === 'custom' && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" justifyContent="space-between" mt={2}>
              <DatePicker
                label="Start Date"
                value={customStartDate}
                onChange={setCustomStartDate}
                renderInput={(params) => <TextField {...params} />}
              />
              <DatePicker
                label="End Date"
                value={customEndDate}
                onChange={setCustomEndDate}
                renderInput={(params) => <TextField {...params} />}
              />
            </Box>
          </LocalizationProvider>
        )}
      </Box>}

        
      </Box>

      <Pagination
        count={Math.ceil(activities.length / itemsPerPage)}
        page={currentPage}
        onChange={handlePageChange}
      />
    </Box>
  );
};

export default ActivityPage;
